import Big from 'big.js'
import { assertMultiply } from 'utils'
import { Address, zeroAddress } from 'viem'
import { pricesRefreshInterval } from './config'
import { InternalInconsistencyError, UnderflowError } from './errors'
import type {
  AssetsConfigType,
  ChainAsset,
  CreatedRanges,
  PortfolioCache,
  PortfolioPrice,
  PortfolioPrices,
  PortfolioRangeData,
  Portfolios,
  PriceRangeStore,
  PricesStore,
  RangeMap,
} from './interfaces'
import {
  dayIndexToRangeIndex,
  dayIndexToTimestamp,
  isInsideCachedRange,
  requestHistoricalPrice,
  requestLastSyncedTimestamps,
  timestampToDayIndex,
  updateRange,
} from './utils'

export const createRanges = (
  config: AssetsConfigType,
  portfolios: Portfolios[],
  getPricesStore: () => PricesStore,
  getPriceRangesStore: () => PriceRangeStore,
): CreatedRanges => {
  // cached data from previously successful requests
  const cachedPrices = getPricesStore()
  const cachedPriceRanges = getPriceRangesStore()

  const now = Math.floor(new Date().getTime() / 1000)
  const currentDayId = timestampToDayIndex(config, now)
  const portfolioCache: PortfolioCache[] = []

  // temporary data for forming new price range requests
  const priceRanges: PriceRangeStore = {}
  let latestSyncProductTimestamp = 0

  // part 1 - create intersecting ranges

  for (let i = 0; i < portfolios.length; i++) {
    const portfolio = portfolios[i]

    if (portfolio.assets?.[`${portfolio.chainID}:${zeroAddress}`] === undefined) {
      portfolio.assets[`${portfolio.chainID}:${zeroAddress}`] = {
        chainId: Number.parseInt(portfolio.chainID),
        value: Big(0),
      }
    }

    const productKeys = Object.keys(portfolios[i].assets)
    let lastSyncProductTimestamp = 0
    for (const sym of productKeys) {
      const symbol = sym as ChainAsset
      if (lastSyncProductTimestamp === 0) {
        lastSyncProductTimestamp = config.assets[symbol].lastSyncTimestamp
      } else {
        lastSyncProductTimestamp = Math.min(
          lastSyncProductTimestamp,
          config.assets[symbol].lastSyncTimestamp,
        )
        latestSyncProductTimestamp = Math.max(
          lastSyncProductTimestamp,
          config.assets[symbol].lastSyncTimestamp,
        )
      }
    }

    const rangeStart = portfolio.timestamp
    const rangeEnd = i === portfolios.length - 1 ? now : portfolios[i + 1].timestamp
    const min =
      config.assets[`${+portfolio.chainID}:${portfolio.index as Address}`].leastPossibleTimestamp
    const max =
      config.assets[`${+portfolio.chainID}:${portfolio.index as Address}`].greatestPossibleTimestamp

    if (rangeStart < min || rangeEnd > max) {
      continue
    }
    // portfolios must be sorted by timestamp in ascending order
    if (rangeStart > rangeEnd) {
      throw new InternalInconsistencyError()
    }

    const portfolioRangeData: PortfolioRangeData[] = []
    let subrangeStart = rangeStart
    while (subrangeStart < rangeEnd) {
      const nextDayIndex = timestampToDayIndex(
        config,
        subrangeStart + config.refreshTimeIntervalGranularity,
      )
      const nextRangeStart = dayIndexToTimestamp(config, nextDayIndex)

      if (nextRangeStart === 0) {
        throw new UnderflowError(nextRangeStart, 1)
      }
      const subrangeEnd = Math.min(nextRangeStart - 1, rangeEnd)

      if (nextDayIndex === 0) {
        throw new UnderflowError(nextDayIndex, 1)
      }
      const dayIndex = nextDayIndex - 1
      const endDayIndex = timestampToDayIndex(config, subrangeEnd)

      const rangeIndex = dayIndexToRangeIndex(config, dayIndex)

      const insideCachedProductRange = isInsideCachedRange(
        config,
        cachedPriceRanges,
        `${+portfolio.chainID}:${portfolio.index as Address}`,
        rangeIndex,
        subrangeStart,
        subrangeEnd,
        now,
        latestSyncProductTimestamp,
      )

      portfolioRangeData.push({
        range: [subrangeStart, subrangeEnd],
        startDayIndex: dayIndex,
        endDayIndex: endDayIndex,
        rangeIndex: rangeIndex,
      })
      // don't iterate if a cached portfolio range exists

      if (!insideCachedProductRange) {
        // don't iterate if a cached portfolio range exists
        for (const token of Object.keys(portfolio.assets)) {
          const asset = token as ChainAsset
          if (
            subrangeStart < config.assets[asset].leastPossibleTimestamp ||
            subrangeEnd > config.assets[asset].greatestPossibleTimestamp
          ) {
            // asset cannot be outside existing portfolio range
            throw new InternalInconsistencyError()
          }

          const lastSyncAssetTimestamp = config.assets[asset].lastSyncTimestamp
          const lastSyncAssetDayId = timestampToDayIndex(config, lastSyncAssetTimestamp)

          const insideCachedRange = isInsideCachedRange(
            config,
            cachedPriceRanges,
            asset,
            rangeIndex,
            subrangeStart,
            subrangeEnd,
            now,
            lastSyncAssetTimestamp,
          )
          if (insideCachedRange) {
            // asset is inside of the cached range, don't add it into the request list
            if (
              lastSyncAssetDayId === currentDayId &&
              now - lastSyncAssetTimestamp > pricesRefreshInterval
            ) {
              // need to resync the price for the current dayId
              updateRange(priceRanges, asset, rangeIndex, subrangeStart, subrangeEnd)
            }

            if (
              lastSyncAssetDayId < currentDayId &&
              now - lastSyncAssetTimestamp > pricesRefreshInterval
            ) {
              // we have the price, but it might not be the last price. need to resync anyways
              updateRange(priceRanges, asset, rangeIndex, subrangeStart, subrangeEnd)
            }

            continue
          }
          updateRange(priceRanges, asset, rangeIndex, subrangeStart, subrangeEnd)
          latestSyncProductTimestamp = subrangeEnd
        }
      }
      subrangeStart = nextRangeStart
    }

    // only add portfolios for valid ranges
    if (portfolioRangeData.length !== 0) {
      portfolioCache.push({
        portfolio: portfolio,
        rangeData: portfolioRangeData,
      })
    }
  }

  return {
    now,
    priceRanges,
    portfolioCache,
    cachedPriceRanges,
    cachedPrices,
    lastSyncProductTimestamp: latestSyncProductTimestamp,
  }
}

export const createBatches = async (
  config: AssetsConfigType,
  priceRanges: PriceRangeStore,
  cachedPrices: PricesStore,
  cachedPriceRanges: PriceRangeStore,
  currentDayId: number,
) => {
  let filledRangeBatches = 0
  let filledAssets = 0
  let filledRequestParams: RangeMap = {}

  const priceRangeKeys = Object.keys(priceRanges) as ChainAsset[]
  let isError = false
  for (let i = 0; i < priceRangeKeys.length; i++) {
    const asset = priceRangeKeys[i]

    const priceRangeEntries = Object.entries(priceRanges[asset])

    for (let j = 0; j < priceRangeEntries.length; j++) {
      const [rangeIndex_, range] = priceRangeEntries[j]
      const rangeIndex = +rangeIndex_

      if (filledRequestParams[asset] === undefined) {
        filledRequestParams[asset] = []
        filledAssets++
      }

      const cachedRange = cachedPriceRanges[asset]?.[rangeIndex]

      if (cachedRange !== undefined) {
        const remainingRanges = getRemainingRanges(range, cachedRange)
        const cachedEndDayId = timestampToDayIndex(config, cachedRange[1])
        const rangeEndDayId = timestampToDayIndex(config, range[1])
        if (
          remainingRanges.length > 0 &&
          cachedEndDayId === rangeEndDayId &&
          cachedPrices[asset][currentDayId] === undefined
        ) {
          remainingRanges[remainingRanges.length - 1][0] =
            range[0] - config.refreshTimeIntervalGranularity
        }

        remainingRanges.forEach(([start, end]) => {
          filledRequestParams[asset].push([start, end])
          filledRangeBatches++
        })
      } else {
        // no cached range, make a full range request
        if (
          filledRequestParams[asset].length !== 0 &&
          filledRequestParams[asset][filledRequestParams[asset].length - 1][0] === range[0]
        ) {
          filledRequestParams[asset].pop()
        }
        filledRequestParams[asset].push(range)

        filledRangeBatches++
      }

      if (
        filledRangeBatches >= config.maximumRequestableRangeBatches ||
        filledAssets >= config.maximumRequestableAssets ||
        (i === priceRangeKeys.length - 1 && j === priceRangeEntries.length - 1)
      ) {
        if (filledRangeBatches !== 0 && filledAssets !== 0) {
          const filteredRequestParams: RangeMap = {}
          const testFilteredRequestParams: RangeMap = {}
          let isMock = false
          for (const [key, value] of Object.entries(filledRequestParams)) {
            if (value.length !== 0) {
              filteredRequestParams[key as ChainAsset] = value
              if (key !== '8453:0x100ce3e3391c00b6a52911313a4ea8d23c8a38d8') {
                testFilteredRequestParams[key as ChainAsset] = value
              } else {
                isMock = true
              }
            }
          }
          //
          const { data: pricesForAssets, errors } =
            await requestHistoricalPrice(testFilteredRequestParams)
          if (errors !== undefined) {
            isError = true
          }

          if (isMock) {
            pricesForAssets['8453:0x100ce3e3391c00b6a52911313a4ea8d23c8a38d8'] = Object.entries(
              pricesForAssets,
            )[0][1].map((arr) => arr.map((el) => ({ ...el, price: '0.000051' })))
          }

          for (const sym in pricesForAssets) {
            const assetSymbol = sym as ChainAsset
            const priceBatches = pricesForAssets[assetSymbol]

            // update cache
            for (const priceBatch of priceBatches) {
              if (cachedPrices[assetSymbol] === undefined) {
                cachedPrices[assetSymbol] = {}
              }
              // update cached prices
              for (const price of priceBatch) {
                const dayIndex = timestampToDayIndex(config, price.timestamp)
                cachedPrices[assetSymbol][dayIndex] = price.price
              }

              // update cached ranges to include newly extended bounds
              const filledRanges = filledRequestParams[assetSymbol as ChainAsset] // TODO: fix
              for (const filledRange of filledRanges) {
                const dayIndex = timestampToDayIndex(config, filledRange[0])
                const rangeIndex = dayIndexToRangeIndex(config, dayIndex)
                updateRange(
                  cachedPriceRanges,
                  assetSymbol,
                  rangeIndex,
                  filledRange[0],
                  filledRange[1],
                )
              }
            }
          }
        }

        // clear request batch
        filledRequestParams = {}
        filledRangeBatches = 0
        filledAssets = 0
      }
    }
  }
  return {
    updatedCachedPriceRanges: cachedPriceRanges,
    updatedCachedPrices: cachedPrices,
    isError: isError,
  }
}

// part 3 - calculate prices and valuation from cached prices
export const createPrices = async (
  config: AssetsConfigType,
  portfolios: Portfolios[],
  getPricesStore: () => PricesStore,
  getPriceRangesStore: () => PriceRangeStore,
  writePriceStore: (data: PricesStore) => void,
  writePriceRangesStore: (data: PriceRangeStore) => void,
): Promise<{ prices: PortfolioPrices; isError: boolean }> => {
  const lastSyncedTimestamps = await requestLastSyncedTimestamps()
  const now = Math.floor(new Date().getTime() / 1000)
  const currentDayId = timestampToDayIndex(config, now)
  for (const sym in lastSyncedTimestamps) {
    const symbol = sym as ChainAsset
    if (config.assets[symbol] !== undefined) {
      config.assets[symbol].lastSyncTimestamp = lastSyncedTimestamps[symbol]
    }
  }

  const {
    priceRanges: ranges,
    portfolioCache,
    cachedPriceRanges,
    cachedPrices,
    lastSyncProductTimestamp,
  } = createRanges(config, portfolios, getPricesStore, getPriceRangesStore)

  const { updatedCachedPriceRanges, updatedCachedPrices, isError } = await createBatches(
    config,
    ranges,
    cachedPrices,
    cachedPriceRanges,
    currentDayId,
  )

  if (isError) {
    return { prices: {}, isError: true }
  }

  let portfolioPrices: PortfolioPrices = {}
  for (const { portfolio, rangeData } of portfolioCache) {
    if (rangeData.length === 0) {
      // portfolio cache without ranges should never be created
      throw new InternalInconsistencyError()
    }

    for (const { range, rangeIndex, startDayIndex, endDayIndex } of rangeData) {
      for (let dayIndex = startDayIndex; dayIndex <= endDayIndex; dayIndex++) {
        let price: string
        if (
          updatedCachedPrices[`${portfolio.chainID}:${portfolio.index as Address}`] === undefined
        ) {
          updatedCachedPrices[`${portfolio.chainID}:${portfolio.index as Address}`] = {}
        }
        const cachedPrice =
          updatedCachedPrices[`${portfolio.chainID}:${portfolio.index as Address}`][dayIndex]
        if (cachedPrice !== undefined && dayIndex < currentDayId - 1) {
          price = cachedPrice
        } else {
          // recompute the portfolio price using cached asset prices
          let marketCapitalization: Big = Big(0)
          for (const asset in portfolio.assets) {
            const balance = portfolio.assets[asset].value
            const price_ = updatedCachedPrices[asset]?.[dayIndex]
            if (price_ === undefined) {
              console.log(asset, dayIndex)
              // if the price is not set it was erroneously filtered, didn't get requested or didn't get updated after the request
              throw new InternalInconsistencyError()
            }
            assertMultiply(balance, price_)
            marketCapitalization = marketCapitalization.add(Big(balance).mul(price_))
          }
          price = Big(portfolio.totalSupply).eq(0)
            ? '0'
            : marketCapitalization.div(portfolio.totalSupply).toString()

          marketCapitalization = Big(0)
          updatedCachedPrices[`${portfolio.chainID}:${portfolio.index as Address}`][dayIndex] =
            price
        }

        if (portfolioPrices[`${+portfolio.chainID}:${portfolio.index as Address}`] === undefined) {
          portfolioPrices[`${+portfolio.chainID}:${portfolio.index as Address}`] = []
        }
        portfolioPrices[`${+portfolio.chainID}:${portfolio.index as Address}`].push({
          value: Big(price),
          timestamp: range[0],
        })
        // cache portfolio range
        updateRange(
          updatedCachedPriceRanges,
          `${+portfolio.chainID}:${portfolio.index as Address}`,
          rangeIndex,
          range[0],
          range[1],
        )
      }
    }

    const productPort = portfolioPrices[`${+portfolio.chainID}:${portfolio.index as Address}`]
    const lastPoint =
      timestampToDayIndex(config, productPort[productPort.length - 1].timestamp) === currentDayId
    if (lastPoint) {
      portfolioPrices = {
        [`${portfolio.chainID}:${portfolio.index}`]: [
          ...productPort.slice(0, productPort.length - 1),
          {
            value: productPort[productPort.length - 1].value,
            timestamp: lastSyncProductTimestamp,
          } as PortfolioPrice,
        ],
      }
    }
  }

  writePriceStore(updatedCachedPrices)
  writePriceRangesStore(updatedCachedPriceRanges)

  return { prices: portfolioPrices, isError: false }
}

function getRemainingRanges(
  mainRange: [number, number],
  subRange: [number, number],
): [number, number][] {
  const [mainMin, mainMax] = mainRange
  const [subMin, subMax] = subRange

  const remainingRanges: [number, number][] = []

  if (subMin > mainMin) {
    remainingRanges.push([mainMin, subMin - 1])
  }

  if (subMax < mainMax) {
    remainingRanges.push([subMax + 1, mainMax])
  }

  return remainingRanges
}
